import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";

export interface ResearchRun {
  id: string;
  topic: string | null;
  run_date: string;
  status: string;
  keywords_researched_count: number;
  findings_count: number;
  opportunities_count: number;
  content_ideas_count: number;
  threats_count: number;
  keywords_extracted: string[] | null;
  created_at: string;
}

export interface ResearchRunDetail extends ResearchRun {
  report_md: string | null;
  findings_json: unknown[] | null;
  keyword_seeds: string[] | null;
}

export interface ResearchPackageStageState {
  stage:
    | "market_research_report"
    | "keyword_master"
    | "strategy_overlay"
    | "execution_plan"
    | "persona_research"
    | "pain_point_map"
    | "positioning_angles"
    | "page_production";
  status: "not_started" | "generated" | "needs_review" | "approved" | "superseded";
  document_key: string | null;
  artifact_key: string | null;
  workflow_instance_id?: string | null;
  pending_gate_id?: string | null;
}

export interface ResearchPackageValidationIssue {
  kind: "report" | "retrieval" | "source";
  code: string;
  message: string;
  finding_title?: string | null;
}

export interface ResearchPackageSourceValidation {
  ready: boolean;
  workflow_run_id: string | null;
  findings_count: number;
  report_ready: boolean;
  issues: ResearchPackageValidationIssue[];
}

export interface ResearchPackageDocument {
  document_key: string;
  brand_id: string;
  slug:
    | "research-keyword-master"
    | "research-strategy-overlay"
    | "research-execution-plan"
    | "persona-research"
    | "pain-point-map"
    | "positioning-angles"
    | "page-strategy-inference"
    | "page-quality-criteria"
    | "page-outline"
    | "page-wireframe-markdown"
    | "page-copy-markdown"
    | "page-html-draft"
    | "page-voice-review"
    | "page-grade-report"
    | "page-seo-optimized";
  timestamp_ms: number;
  content: string;
  source: "agent" | "human";
  task_id: string | null;
  model: string | null;
  skill_files: string[];
  created_at: string;
}

export interface ResearchPackage {
  id: string;
  brand_id: string;
  source_run_id: string;
  market_research_artifact_key: string;
  status: "not_started" | "generated" | "needs_review" | "approved" | "superseded";
  stages: ResearchPackageStageState[];
  source_validation?: ResearchPackageSourceValidation | null;
  created_at: string;
  updated_at: string;
}

export function useResearchRuns(brandId: string | undefined) {
  return useQuery({
    queryKey: ["brands", brandId, "research"],
    queryFn: () => api.get<{ data: ResearchRun[] }>(`/api/brands/${brandId}/research`),
    enabled: !!brandId,
  });
}

export function useResearchRun(brandId: string | undefined, runId: string | undefined) {
  return useQuery({
    queryKey: ["brands", brandId, "research", runId],
    queryFn: () => api.get<{ data: ResearchRunDetail }>(`/api/brands/${brandId}/research/${runId}`),
    enabled: !!brandId && !!runId,
  });
}

export function useRunResearch(brandId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ prompt }: { prompt?: string }) =>
      api.post<{ run_id: string }>(`/api/brands/${brandId}/tasks`, {
        task_slug: "market_research",
        params: prompt ? { prompt } : {},
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["brands", brandId, "research"] });
      setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: ["brands", brandId, "research"] });
      }, 2000);
    },
  });
}

export function useCreateResearchPackage(brandId: string | undefined) {
  return useMutation({
    mutationFn: async ({ runId }: { runId: string }) => {
      if (!brandId) {
        throw new Error("Brand context is required to create a research package.");
      }
      return api.post<{ data: ResearchPackage }>(`/api/brands/${brandId}/research/packages`, {
        run_id: runId,
      });
    },
  });
}

export function useResearchPackage(brandId: string | undefined, packageId: string | undefined) {
  return useQuery({
    queryKey: ["brands", brandId, "research", "packages", packageId],
    queryFn: () => api.get<{ data: ResearchPackage }>(`/api/brands/${brandId}/research/packages/${packageId}`),
    enabled: !!brandId && !!packageId,
  });
}

export function useGenerateKeywordMaster(
  brandId: string | undefined,
  packageId: string | undefined,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (!brandId || !packageId) {
        throw new Error("Package context is required to generate a keyword master.");
      }
      return api.post<{ data: { package: ResearchPackage; document: ResearchPackageDocument } }>(
        `/api/brands/${brandId}/research/packages/${packageId}/keyword-master`,
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["brands", brandId, "research", "packages", packageId],
      });
    },
  });
}

export function useGenerateStrategyOverlay(
  brandId: string | undefined,
  packageId: string | undefined,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (!brandId || !packageId) {
        throw new Error("Package context is required to generate a strategy overlay.");
      }
      return api.post<{ data: { package: ResearchPackage; document: ResearchPackageDocument } }>(
        `/api/brands/${brandId}/research/packages/${packageId}/strategy-overlay`,
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["brands", brandId, "research", "packages", packageId],
      });
    },
  });
}

export function useGenerateExecutionPlan(
  brandId: string | undefined,
  packageId: string | undefined,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (!brandId || !packageId) {
        throw new Error("Package context is required to generate an execution plan.");
      }
      return api.post<{ data: { package: ResearchPackage; document: ResearchPackageDocument } }>(
        `/api/brands/${brandId}/research/packages/${packageId}/execution-plan`,
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["brands", brandId, "research", "packages", packageId],
      });
    },
  });
}

export function useGeneratePersonaResearch(
  brandId: string | undefined,
  packageId: string | undefined,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (!brandId || !packageId) {
        throw new Error("Package context is required to generate persona research.");
      }
      return api.post<{ data: { package: ResearchPackage; workflow: unknown; document: ResearchPackageDocument | null } }>(
        `/api/brands/${brandId}/research/packages/${packageId}/persona-research`,
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["brands", brandId, "research", "packages", packageId],
      });
      void queryClient.invalidateQueries({ queryKey: ["human-gates"] });
    },
  });
}

export function useGeneratePainPointMap(
  brandId: string | undefined,
  packageId: string | undefined,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (!brandId || !packageId) {
        throw new Error("Package context is required to generate a pain-point map.");
      }
      return api.post<{ data: { package: ResearchPackage; workflow: unknown; document: ResearchPackageDocument | null } }>(
        `/api/brands/${brandId}/research/packages/${packageId}/pain-point-map`,
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["brands", brandId, "research", "packages", packageId],
      });
      void queryClient.invalidateQueries({ queryKey: ["human-gates"] });
    },
  });
}

export function useGeneratePositioningAngles(
  brandId: string | undefined,
  packageId: string | undefined,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (!brandId || !packageId) {
        throw new Error("Package context is required to generate positioning.");
      }
      return api.post<{ data: { package: ResearchPackage; workflow: unknown; document: ResearchPackageDocument | null } }>(
        `/api/brands/${brandId}/research/packages/${packageId}/positioning-angles`,
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["brands", brandId, "research", "packages", packageId],
      });
      void queryClient.invalidateQueries({ queryKey: ["human-gates"] });
    },
  });
}

export function useStartPageProduction(
  brandId: string | undefined,
  packageId: string | undefined,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { targetKeyword: string; pageType?: string; pageIntent?: string }) => {
      if (!brandId || !packageId) {
        throw new Error("Package context is required to start page production.");
      }
      return api.post<{ data: { package: ResearchPackage; workflow: unknown; document: ResearchPackageDocument | null } }>(
        `/api/brands/${brandId}/research/packages/${packageId}/page-production`,
        payload,
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["brands", brandId, "research", "packages", packageId],
      });
      void queryClient.invalidateQueries({ queryKey: ["human-gates"] });
    },
  });
}
